"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.careByEllaTenantConfig = void 0;
exports.jwtFallbackRefusal = jwtFallbackRefusal;
// Care by Ella — first tenant.
//
// Values prefixed with TODO are placeholders until the corresponding accounts /
// agreements are in place (Stripe products, BankID prod cert, Webdoc partner
// credentials). Video (Daily.co) is configured via server env vars, not here.
// Dev-only fallback for `jwtSigningKey`. Long enough to satisfy the Zod
// min(32) check so `npm run dev` works out of the box without an env file.
// Wherever real people can sign in, or real money moves, we fail fast at
// module load (below) instead. See `docs/ops/jwt-rotation.md`.
const INSECURE_DEV_JWT_KEY = "<insecure-dev-default-32-chars-12345>";
function readEnv(key) {
    return typeof process !== "undefined" ? process.env?.[key] : undefined;
}
const isNode = typeof process !== "undefined" && typeof process.exit === "function";
// TES-649 — when the insecure fallback must be refused. It used to be only
// `NODE_ENV === "production"`, and QA runs NODE_ENV=development while signing
// in real people with live BankID: a QA box without JWT_SIGNING_KEY would have
// signed its sessions with a key that is public in this repository, so anyone
// could mint a staff token for it. Like the ENCRYPTION_* rule in
// apps/api/src/config/env.ts (TES-716), this is bound to facts about the
// integrations rather than to the run mode, so a mistyped NODE_ENV cannot
// switch it off. Returns why the fallback is refused, or null when it may be
// used. Exported for the spec; `read` defaults to the process environment.
function jwtFallbackRefusal(read = readEnv) {
    const key = read("JWT_SIGNING_KEY");
    if (key && key !== INSECURE_DEV_JWT_KEY)
        return null;
    const live = [];
    if (read("NODE_ENV") === "production")
        live.push("NODE_ENV=production");
    // BANKID_ENVIRONMENT only switches certificate verification; the endpoint
    // comes from BANKID_BASE_URL. Either one naming production means real
    // identities.
    if (read("BANKID_ENVIRONMENT") === "production") {
        live.push("BANKID_ENVIRONMENT=production");
    }
    if (/^https:\/\/appapi2\.bankid\.com(\/|$)/.test(read("BANKID_BASE_URL") ?? "")) {
        live.push("BANKID_BASE_URL is the live BankID endpoint");
    }
    if (/_live_/.test(read("STRIPE_SECRET_KEY") ?? "")) {
        live.push("STRIPE_SECRET_KEY is a live key");
    }
    if (live.length === 0)
        return null;
    return (`JWT_SIGNING_KEY is ${key ? "the insecure dev default" : "unset"} while ` +
        `live integrations are configured (${live.join(", ")})`);
}
const jwtRefusal = isNode ? jwtFallbackRefusal() : null;
if (jwtRefusal) {
    // eslint-disable-next-line no-console
    console.error(`[care-by-ella] FATAL: ${jwtRefusal}. Refusing to sign sessions with a ` +
        "key that is public in the repository. Set JWT_SIGNING_KEY in the " +
        "environment (see docs/ops/jwt-rotation.md).");
    process.exit(1);
}
exports.careByEllaTenantConfig = {
    id: "care-by-ella",
    name: "care-by-ella",
    displayName: "Ella",
    domains: {
        // real domains decided (ella.se); baked into every mobile build since
        // #419. TES-551: the legacy names on pelag.io are gone.
        apiUrl: "https://api.ella.se",
        webUrl: "https://ella.se",
        // TES-464 (Ö-91): the legacy store records are reused —
        // se.ella.app (App Store id 1615543443, live since 2022) and se.ella.android.
        // Irreversible once 2.0.0 ships; the Android namespace stays com.carebyella
        // (applicationId is the store identity, namespace only the R class).
        appBundleIdIos: "se.ella.app",
        appBundleIdAndroid: "se.ella.android",
        appDisplayName: "Ella",
    },
    locales: {
        supported: ["sv", "en"],
        default: "sv",
    },
    // Sentry (her-company-ab/react-native, EU ingest) — baked in at build;
    // enables crash and error reporting in the app from the next TestFlight.
    // The backend's DSN (care-by-ella-api) lives in the box's secrets.env.
    sentryDsn: "https://d83cb17d92c823ab7e41f8202c32d75f@o4511983062876160.ingest.de.sentry.io/4511983085617232",
    bankid: {
        certPath: "tenants/care-by-ella/secrets/bankid-prod.p12", // not committed
        trustPath: "tenants/care-by-ella/secrets/bankid-trust.ca",
        requiredOidPolicy: "1.2.752.78.1.5", // production policy
        autoStartTokenAppLinkScheme: "carebyella://bankid",
    },
    stripe: {
        // Stripe TEST-mode keys/prices (2026-08-26, CAR-39 flip). Public key only —
        // the secret key + webhook secret live in the box secrets.env.
        publishableKey: "pk_test_51U6ResBhAEqnqPUWNCPLrWeJXf3dqwZpvTgGFgjUZ0OEv0nu8945n6oF70jWH8dxy5H1upiT78ddAgIS2Q1iBWTf00MoB5cUYQ",
        // "Ella Core" 995 SEK/month.
        coreSubscriptionPriceId: "price_1U8ayGBhAEqnqPUW2gU7XJ0x",
        // "Ella Underhåll" 99 SEK/month.
        maintenancePriceId: "price_1U8ayqBhAEqnqPUWXocbmChq",
    },
    partnerClinic: {
        id: "partner-clinic-001",
        // There is no partner clinic: Her Company AB is itself registered as a
        // care provider ("vårdgivare") with IVO (TES-475 Ö-1, 2026-09-09). `name` stays unset;
        // the public trust line names Ella's own IVO registration instead.
        supportedLocales: ["sv", "en"],
        webdocClinicRef: undefined, // set once Webdoc partnership lands
    },
    webdoc: {
        enabled: true,
        baseUrl: "https://api.webdoc.example", // TODO: real CGM/Webdoc endpoint once partnership confirmed
        // CAR-51: writing anamnesis content into the patient journal stays off
        // until the DPA/DPIA (CAR-27) clears the data-processor boundary.
        journalNoteWrites: false,
    },
    // The standard patient flow shows SYNLAB only (TES-1342, CP-06 "SLUTLIG
    // KANONISK LABBRUTIN", 2026-10-14): "Unilabs får inte visas i standardflödet
    // utan ett nytt uttryckligt produkt- och verksamhetsbeslut." The location
    // lists, with map, address and directions, are owned and maintained by the lab.
    labPartners: [
        {
            id: "synlab",
            name: "SYNLAB",
            integrationMode: "doctor_issued_remiss", // the doctor issues the referral via Webdoc
            links: [
                {
                    labelSv: "SYNLABs närlabb",
                    url: "https://synlab.se/patient/har-finns-vi/narlab",
                },
                {
                    labelSv: "SYNLABs provtagningspartners",
                    url: "https://synlab.se/patient/har-finns-vi/provtagningspartners",
                },
            ],
        },
    ],
    pricing: {
        currencyCode: "SEK",
        coreSubscriptionMinor: 99500, // 995.00 SEK incl. VAT
        // 695.00 SEK incl. VAT — "Ella Underhåll" (TES-770); the live Stripe
        // price, confirmed by Simon 2026-10-26 (TES-1599, Ö-942).
        maintenanceMinor: 69500,
        // Must equal the API's CONSULT_FEE_CENTS (apps/api/src/config/env.ts).
        consultationMinor: 39500, // 395.00 SEK incl. VAT — digital care meeting
        labTestMinor: 99500, // 995.00 SEK incl. VAT — nytt blodprov (TES-720, Simon 2026-09-16)
    },
    support: {
        email: "support@carebyella.se",
    },
    // CAR-121 — coach persona (Ö-38). The coach is named, and this one value
    // drives both the chat sender label and the delivery-time `[coach]`
    // substitution in coach_message bodies: renaming is this line, never a code
    // change.
    //
    // `avatarMediaKey` (TES-485, Ö-37/Ö-46) names the portrait on our own media
    // platform. Until the asset is uploaded (Ö-36) the key 404s and every client
    // falls back to its bundled avatar. Never a third-party URL again.
    coach: {
        displayName: "Emelie",
        title: "Hälsocoach",
        avatarMediaKey: "care_team.coach",
    },
    // Doctor persona — tenant-level identity, parallel to the coach.
    //
    // TES-713 (Simon 2026-09-16): the doctor IS NAMED Kirill. The field used to
    // carry the string "Din läkare" — i.e. a ROLE in the name field — and since
    // every surface renders `displayName` as a name, the patient got a card
    // headed "Din läkare" next to one headed "Emelie". Those are not two ways of
    // saying the same thing; they are a name and a title in the same position.
    //
    // The old comment said the patient never sees a per-doctor name. That has
    // been overturned since Ö-36 (2026-09-02): the care team is shown by name,
    // with portraits. The role now stands next to the name instead of replacing
    // it.
    //
    // NOTE that the config carries ONE doctor per tenant. If the care meeting is
    // with someone else the name no longer fits — see TES-713 for that question.
    doctor: {
        displayName: "Kirill",
        title: "Läkare",
        avatarMediaKey: "care_team.doctor",
    },
    // TES-698 / TES-676 — the fallback route for the acute alarm. Simon
    // 2026-09-16: "the number is 120 and the person is emelie, outside of office
    // hours its nobody". Three decisions, three lines — none of them is code.
    acuteEscalation: {
        slaMinutes: 120,
        fallbackEmail: "emelie@ella.se",
        officeHours: {
            timeZone: "Europe/Stockholm",
            weekdays: [1, 2, 3, 4, 5],
            startHour: 8,
            endHour: 17,
        },
    },
    features: {
        enableLabResultsVisualization: true,
        enableMaintenanceTier: false, // ship core sub first; turn on once retention layer is built
        enableEnglishOnboarding: true,
        // TES-1601, on since TES-1642 (TES-1600 S7) in every environment: QA and
        // prod import this same file. It requires REQUIRED_CARE_MEETING_FEE_CENTS=0
        // in the box secrets, because the terms promise that a meeting the doctor
        // requires is free (Ö-947); unset, that meeting costs the consult fee.
        careMeetingOptional: true,
        // TES-1656 — TES-1642 merged and went on on this day (#1804).
        careMeetingOptionalSince: "2026-10-30",
        // TES-1554 — off in every environment (QA and prod import this same
        // file). Flipping it waits on Zeinab (Ö-860) and Kirill (Ö-862).
        unsuitableEndRefundTask: false,
    },
    // Per-tenant JWT signing key (CAR-29). The env var is the prod source; the
    // dev default keeps `npm run dev` working without an .env file. Use next to
    // live integrations is refused by the `process.exit(1)` guard above.
    jwtSigningKey: readEnv("JWT_SIGNING_KEY") ?? INSECURE_DEV_JWT_KEY,
};
exports.default = exports.careByEllaTenantConfig;
//# sourceMappingURL=tenant.config.js.map